import type { ConnectorPermissionLevel } from '@research-assistant/shared';
import { supportsConnectorWrite } from '@research-assistant/shared';
import { Loader2Icon, TriangleAlertIcon, XIcon } from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { DialogClose } from '@/components/ui/dialog.js';
import { Label } from '@/components/ui/label.js';
import { Switch } from '@/components/ui/switch.js';

/**
 * What the agent may do through one connection. Read is always granted, so it is not part of it:
 * `permissionLevel` is the Allow edit switch and `allowDelete` the Allow delete switch.
 */
export interface ConnectorAccess {
  permissionLevel: ConnectorPermissionLevel;
  allowDelete: boolean;
}

/** Connectors that only read by design, so Allow edit does not apply to them. */
const READ_ONLY_CONNECTORS: ReadonlySet<string> = new Set([
  'powerbi',
  'reportportal',
]);

/** Connectors whose agent tools include a delete. Allow delete does not apply to the rest. */
const DELETE_CONNECTORS: ReadonlySet<string> = new Set([
  'outlook',
  'sharepoint',
  'xray',
]);

/** True when the Allow edit switch means something for this connector. */
export function editApplies(provider: string): boolean {
  return !READ_ONLY_CONNECTORS.has(provider);
}

/** True when the Allow delete switch means something for this connector. */
export function deleteApplies(provider: string): boolean {
  return DELETE_CONNECTORS.has(provider);
}

/** The access a new connection starts with: everything on, except Power BI, which only reads. */
export function defaultConnectorAccess(provider: string): ConnectorAccess {
  return supportsConnectorWrite(provider)
    ? { permissionLevel: 'read_write', allowDelete: true }
    : { permissionLevel: 'read', allowDelete: false };
}

/**
 * The access a sign-in onto an existing connection starts from: what that connection allows now, so an
 * untouched confirm never widens it. An unknown level starts read-only for the same reason.
 */
export function storedConnectorAccess(stored?: {
  permissionLevel?: ConnectorPermissionLevel | null;
  allowDelete?: boolean;
}): ConnectorAccess {
  return {
    permissionLevel: stored?.permissionLevel ?? 'read',
    allowDelete: stored?.allowDelete === true,
  };
}

/**
 * One sentence that says what the agent can do through a connection. Delete needs edit, because
 * every delete the agent has is also a write, so a connection without edit reads as read-only.
 */
export function accessSummary(
  provider: string,
  access: ConnectorAccess,
): string {
  if (!editApplies(provider) || access.permissionLevel !== 'read_write') {
    return 'The agent can only read.';
  }
  return deleteApplies(provider) && access.allowDelete
    ? 'The agent can read, edit and delete.'
    : 'The agent can read and edit.';
}

export interface ConnectorAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * 'connect' = the connect flow, before the sign-in or the credential form; 'edit' = change the access
   * of a live connection, which also says that a token issued without write may need a reconnect.
   */
  mode: 'connect' | 'edit';
  providerId: string;
  providerName: string;
  /** The access the switches start from (re-seeded on every open). */
  initialAccess: ConnectorAccess;
  /** Locks the switches and the buttons while a connect or a save is in flight. */
  busy?: boolean;
  /**
   * Called when the person dismisses the dialog (a click outside or Escape) while it is busy. Given,
   * the dismiss cancels that work and closes the dialog; absent, a busy dialog refuses to close.
   */
  onDismissWhileBusy?: () => void;
  onConfirm: (access: ConnectorAccess) => void | Promise<void>;
}

/** One labelled switch with the sentence that explains it. */
function AccessRow({
  id,
  label,
  hint,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange?: (checked: boolean) => void;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex min-w-0 flex-col gap-1">
        <Label htmlFor={id} className="font-normal">
          {label}
        </Label>
        <p
          id={`${id}-hint`}
          className="text-xs leading-relaxed text-muted-foreground"
        >
          {hint}
        </p>
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-describedby={`${id}-hint`}
      />
    </div>
  );
}

/**
 * The access dialog of every connector: in the connect flow before the sign-in or the credential form,
 * and from the connector card to change a live connection. Allow read is always on. Allow edit and
 * Allow delete are the person's choice wherever the connector has such actions, and a switch that has
 * nothing to act on is shown as not applicable and confirmed as off.
 */
export function ConnectorAccessDialog({
  open,
  onOpenChange,
  mode,
  providerId,
  providerName,
  initialAccess,
  busy = false,
  onDismissWhileBusy,
  onConfirm,
}: ConnectorAccessDialogProps): JSX.Element {
  const [editOn, setEditOn] = useState(
    initialAccess.permissionLevel === 'read_write',
  );
  const [deleteOn, setDeleteOn] = useState(initialAccess.allowDelete);

  useEffect(() => {
    if (!open) return;
    setEditOn(initialAccess.permissionLevel === 'read_write');
    setDeleteOn(initialAccess.allowDelete);
  }, [open, initialAccess.permissionLevel, initialAccess.allowDelete]);

  const canEdit = editApplies(providerId);
  const canDelete = deleteApplies(providerId);
  const editHint = canEdit
    ? `The agent can create and change items in ${providerName}.`
    : `Not applicable: ${providerName} is read-only.`;
  const deleteHint = !canDelete
    ? `Not applicable: the agent has no delete actions in ${providerName}.`
    : editOn
      ? `The agent can delete items in ${providerName}.`
      : 'Turn on Allow edit first.';
  const title = `${providerName} access`;

  return (
    <AppDialog
      open={open}
      onOpenChange={(next) => {
        if (busy && !next) {
          if (!onDismissWhileBusy) return;
          onDismissWhileBusy();
        }
        onOpenChange(next);
      }}
      title={title}
      description={`Choose what the AI agent can do with this ${providerName} connection.`}
      titleHidden
      showCloseButton={false}
      className="flex w-full max-w-[var(--dialog-w-sm)] flex-col gap-0 p-0"
    >
      <div className="px-6 pt-6 pb-2">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold tracking-tight text-foreground">
            {title}
          </h3>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Close"
              disabled={busy}
              className="-mt-1 -mr-1 flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-40"
            >
              <XIcon className="size-4" />
            </button>
          </DialogClose>
        </div>

        <div className="mt-4 flex flex-col gap-4">
          <AccessRow
            id="connector-access-read"
            label="Allow read"
            hint={`The agent can search and read ${providerName} to answer your questions. Always on.`}
            checked
            disabled
          />
          <AccessRow
            id="connector-access-edit"
            label="Allow edit"
            hint={editHint}
            checked={canEdit && editOn}
            disabled={busy || !canEdit}
            onCheckedChange={setEditOn}
          />
          <AccessRow
            id="connector-access-delete"
            label="Allow delete"
            hint={deleteHint}
            checked={canDelete && editOn && deleteOn}
            disabled={busy || !canDelete || !editOn}
            onCheckedChange={setDeleteOn}
          />

          {canEdit && editOn && (
            <div className="flex items-start gap-2 rounded-lg bg-tone-warning/12 px-3 py-2.5">
              <TriangleAlertIcon
                className="mt-0.5 size-4 shrink-0 text-tone-warning"
                aria-hidden="true"
              />
              <p className="text-xs leading-relaxed text-foreground">
                Scheduled runs act unattended and use the access granted here
                without asking you. In a chat, some changes also run without
                your approval.
              </p>
            </div>
          )}

          {mode === 'edit' && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              Changes apply right away. If the agent still cannot edit,
              reconnect {providerName}.
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 px-6 pb-5 pt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onOpenChange(false)}
          disabled={busy}
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={() =>
            void onConfirm({
              permissionLevel: canEdit && editOn ? 'read_write' : 'read',
              allowDelete: canEdit && canDelete && editOn && deleteOn,
            })
          }
          disabled={busy}
        >
          {busy && (
            <Loader2Icon className="size-3.5 animate-spin" aria-hidden="true" />
          )}
          {mode === 'connect' ? 'Continue' : 'Save'}
        </Button>
      </div>
    </AppDialog>
  );
}
